import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { PublicSidebar } from './PublicSidebar';
import { AppInstallBanner } from '@/components/ui/AppInstallBanner';
import { useScrollDirection } from '@/hooks/useScrollDirection';

export function PublicLayout() {
  const { headerOffset, bottomNavOffset, isDesktop } = useScrollDirection();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Allow scrolling from anywhere on the page (like Twitter)
  useEffect(() => {
    if (!isDesktop) return;

    const handleWheel = (e: WheelEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Check if we're already inside the scroll container
      if (container.contains(e.target as Node)) return;

      // Proxy the scroll to the container
      container.scrollTop += e.deltaY;
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [isDesktop]);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] overflow-x-hidden">
      {/* Centered container for X.com-style layout */}
      <div className="mx-auto max-w-5xl">
        {/* Desktop: fixed sidebar + header, scrollable content */}
        {isDesktop ? (
          <div className="flex h-screen">
            {/* Sidebar - fixed height, doesn't scroll */}
            <PublicSidebar />
            {/* Content area - scroll container with sticky header inside for glass effect */}
            <main
              ref={scrollContainerRef}
              id="main-scroll-container"
              className="flex-1 min-w-0 overflow-y-auto"
            >
              <Header offset={0} isDesktop={isDesktop} />
              <div className="py-4 px-4">
                <Outlet />
              </div>
            </main>
          </div>
        ) : (
          <>
            <Header offset={headerOffset} isDesktop={isDesktop} />
            <main className="pt-[72px] pb-24 px-4">
              <Outlet />
            </main>
            <BottomNav offset={bottomNavOffset} />
            <AppInstallBanner />
          </>
        )}
      </div>
    </div>
  );
}
